import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { DollarSign, Clock, AlertTriangle, CheckCircle2, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatDateMMDDYYYY } from '../utils/timeFormat';

/**
 * BillingOverview
 * Lightweight card for a client's home page that shows at a glance:
 *   - billing model (subscription | pay_per_post | invoiced | deposit)
 *   - payment terms + approval state
 *   - client_type (marketplace | agency)
 *   - any open / upcoming invoice highlights (deposit remaining, balance due date)
 */
const BillingOverview = () => {
  const navigate = useNavigate();
  const [terms, setTerms] = useState(null);
  const [next, setNext] = useState(null);   // next balance due
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requestedTerms, setRequestedTerms] = useState('net_15');
  const [requestNote, setRequestNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refreshTerms = async () => {
    try {
      const termsRes = await api.get('/client/billing/terms');
      setTerms(termsRes.data);
    } catch {}
  };

  const submitNetTermsRequest = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/client/billing/request-terms', {
        requested_terms: requestedTerms,
        note: requestNote?.trim() || undefined,
      });
      toast.success(data?.message || 'Request sent to the billing team.');
      setDialogOpen(false);
      setRequestNote('');
      await refreshTerms();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.statusText || err.message || 'Unknown error';
      toast.error(`Failed to send request: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const termsRes = await api.get('/client/billing/terms');
        if (!active) return;
        setTerms(termsRes.data);
      } catch {}

      try {
        // Best-effort: fetch invoices and pick the nearest unpaid deposit-final or due-soon
        const invRes = await api.get('/invoices/client').catch(() => null);
        if (!active || !invRes?.data) return;
        const invs = Array.isArray(invRes.data) ? invRes.data : invRes.data.invoices || [];
        const today = new Date().toISOString().slice(0, 10);
        // Nearest unpaid invoice by primary_due or final_payment_due
        const upcoming = invs
          .filter((i) => !['paid', 'cancelled', 'void'].includes((i.status || '').toLowerCase()))
          .sort((a, b) => (a.due_date || a.final_payment_due_date || '9999').localeCompare(b.due_date || b.final_payment_due_date || '9999'));
        if (upcoming.length > 0) setNext(upcoming[0]);
        void today; // silence lint
      } catch {}
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  if (loading || !terms) return null;

  const termLabel = {
    due_on_receipt: 'Due on Receipt',
    net_15: 'Net 15',
    net_30: 'Net 30',
  }[terms.payment_terms] || 'Due on Receipt';

  const modelLabel = {
    subscription: 'Subscription',
    pay_per_post: 'Pay per post',
    invoiced: 'Invoiced',
    deposit: 'Deposit-based',
  }[terms.billing_model] || 'Pay per post';

  const isNetTerms = terms.payment_terms !== 'due_on_receipt';
  const hasPendingRequest = isNetTerms && !terms.payment_terms_approved;
  const canRequestTerms = !isNetTerms || !terms.payment_terms_approved;
  const approvalBadge = isNetTerms ? (
    terms.payment_terms_approved ? (
      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3 mr-0.5" /> Approved
      </Badge>
    ) : (
      <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200">
        <AlertTriangle className="w-3 h-3 mr-0.5" /> Pending approval
      </Badge>
    )
  ) : null;

  return (
    <Card className="border-cyan-100 bg-gradient-to-br from-white via-cyan-50/30 to-white" data-testid="billing-overview-card">
      <CardContent className="p-4 sm:p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-cyan-500" />
              <p className="text-sm font-semibold text-gray-900">Billing Overview</p>
            </div>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Your company's payment settings. Contact your account manager to change terms.
            </p>
          </div>
          <button
            onClick={() => navigate('/client/billing')}
            className="text-xs text-cyan-600 hover:text-cyan-800 underline shrink-0"
            data-testid="billing-overview-view-all"
          >
            View invoices
          </button>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-sm">
          <div className="rounded-md bg-white border border-gray-100 px-3 py-2.5 min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">Client type</p>
            <p className="font-medium text-gray-800 capitalize truncate">{terms.client_type}</p>
          </div>
          <div className="rounded-md bg-white border border-gray-100 px-3 py-2.5 min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">Billing model</p>
            <p className="font-medium text-gray-800 truncate">{modelLabel}</p>
          </div>
          <div className="rounded-md bg-white border border-gray-100 px-3 py-2.5 min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">Payment terms</p>
            <p className="font-medium text-gray-800 flex items-center gap-1.5 flex-wrap min-w-0">
              <span className="truncate">{termLabel}</span> {approvalBadge}
            </p>
          </div>
        </div>

        {next && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="next-payment-banner">
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="font-medium">Next payment due</p>
              <p className="text-[11px]">
                {next.payment_structure === 'deposit' && next.balance_due_amount != null
                  ? `Remaining balance $${Number(next.balance_due_amount).toFixed(2)} due by ${formatDateMMDDYYYY(next.final_payment_due_date || next.due_date)}`
                  : `$${Number(next.total_amount || next.amount || 0).toFixed(2)} due ${formatDateMMDDYYYY(next.due_date || next.primary_due_date) || 'soon'}`}
              </p>
            </div>
          </div>
        )}

        {/* Explainer — so the client understands without opening an invoice */}
        <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
          {isNetTerms && terms.payment_terms_approved ? (
            <>Invoices are due {termLabel.toLowerCase()} from the issue date.</>
          ) : terms.client_type === 'agency' ? (
            <>Events booked 4+ weeks out → <strong>50% deposit</strong> now, remaining balance 4 days before the event. Events under 4 weeks → <strong>100% due upfront</strong>.</>
          ) : (
            <>Invoices are due on receipt. Pay-per-post invoices are generated when a shift is confirmed.</>
          )}
        </p>

        {canRequestTerms && (
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] text-gray-500">
              {hasPendingRequest
                ? `Your ${termLabel} request is waiting for billing approval.`
                : 'Need invoice-based terms? Request Net 15 or Net 30 below.'}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDialogOpen(true)}
              disabled={hasPendingRequest}
              className="text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="request-net-terms-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              {hasPendingRequest ? 'Request pending' : 'Request Net Terms'}
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="request-net-terms-dialog">
          <DialogHeader>
            <DialogTitle>Request Net Terms</DialogTitle>
            <DialogDescription>
              We'll send your request to the iEvntür billing team. Until it's approved,
              invoices keep the current billing rule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Requested terms</label>
              <div className="flex gap-2">
                {['net_15', 'net_30'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setRequestedTerms(opt)}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition ${
                      requestedTerms === opt
                        ? 'bg-amber-50 border-amber-300 text-amber-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`request-net-terms-option-${opt}`}
                  >
                    {opt === 'net_15' ? 'Net 15' : 'Net 30'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Reason (optional)</label>
              <textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="e.g. Our AP team cuts checks on a Net 30 cycle"
                rows={3}
                maxLength={500}
                className="w-full text-sm border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-amber-200"
                data-testid="request-net-terms-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={submitNetTermsRequest}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="request-net-terms-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BillingOverview;
